import * as React from 'react'
import { Formik } from 'formik'

import { useHandlers } from './hooks'
import { initialValues } from './formConfig'
import { ENTITY_TYPES, SOLD_SERVICE_STATUS } from '@sales-center/common'
import type { ICancelReason } from '@user/store/scheduling'
import { ClosedLostFormFields } from './ClosedLostFormFields'
import { ClosedLostDialogButtons } from './ClosedLostDialogButtons'
import type { ISalesFunnelClosedReasons } from '@user/store/salesFunnel'
import { useCwqAdminClosedLost } from '@user/components/Opportunities/hooks/useCwqAdminClosedLost'
import DialogContent from '@mui/material/DialogContent'
import LinearProgress from '@mui/material/LinearProgress'

type Props = {
  entityId: number
  onClose: () => void
  entityType: ENTITY_TYPES
  hasFreeEstimates: boolean
  salesFunnelStageId: number
  assigneeId: string | null
  cancelReasons: ICancelReason[]
  reasonDetail: ISalesFunnelClosedReasons
  soldServiceStatus?: SOLD_SERVICE_STATUS
}

export function ClosedLostForm(props: Props) {
  const {
    onClose,
    entityId,
    entityType,
    reasonDetail,
    cancelReasons,
    hasFreeEstimates,
    salesFunnelStageId,
    assigneeId,
    soldServiceStatus,
  } = props

  const cwqAdmin = useCwqAdminClosedLost(
    soldServiceStatus ?? SOLD_SERVICE_STATUS.NotSent,
  )
  const showCancelServices = entityType === ENTITY_TYPES.Opportunity && cwqAdmin

  const requireCancelReason = hasFreeEstimates && cancelReasons.length > 0

  const { validate, submitHandler } = useHandlers({
    onClose,
    entityId,
    entityType,
    salesFunnelStageId,
    requireCancelReason,
    reasonDetail,
    assigneeId,
  })

  return (
    <Formik
      validate={validate}
      onSubmit={submitHandler}
      initialValues={initialValues}
    >
      {({ isSubmitting }) => (
        <>
          {isSubmitting && (
            <LinearProgress data-test-id="closed-lost-submit-progress" />
          )}

          <DialogContent sx={{ pt: 0, pb: '20px', px: 3 }}>
            <ClosedLostFormFields
              entityId={entityId}
              reasonDetail={reasonDetail}
              cancelReasons={cancelReasons}
              requireCancelReason={requireCancelReason}
              showCancelServices={showCancelServices}
            />
          </DialogContent>

          <ClosedLostDialogButtons onClose={onClose} />
        </>
      )}
    </Formik>
  )
}
